import { Type, type Static } from "@sinclair/typebox";
import { UuidSchema, MatrixRoomIdSchema } from "./identifiers.ts";
import { AgentKeySchema } from "./registered-agent.ts";

export const USER_AUTOMATION_ROOT_KEY = "io.matrix_idp.agent.automation_root" as const;
// Reserve space for the bounded body preview, metadata and Matrix's federation envelope.
export const USER_AUTOMATION_TEXT_MAX_BYTES = 48_000;
export function supportsUserAutomation(agentKey: string): boolean {
  return agentKey === "knowledge-expert" || agentKey === "browser-expert";
}
const timestamp = Type.String({ format: "date-time" });
const name = Type.String({ minLength: 1, maxLength: 256, pattern: "\\S" });
const prompt = Type.String({ minLength: 1, maxLength: 20_000, pattern: "\\S" });
const timezone = Type.String({ minLength: 1, maxLength: 128 });
const revision = Type.Integer({ minimum: 1, maximum: 2_147_483_647 });
const calendarTime = {
  hour: Type.Integer({ minimum: 0, maximum: 23 }),
  minute: Type.Integer({ minimum: 0, maximum: 59 })
};

export const UserAutomationScheduleSchema = Type.Union([
  Type.Object({ kind: Type.Literal("once"), at: timestamp }, { additionalProperties: false }),
  Type.Object({ kind: Type.Literal("daily"), ...calendarTime }, { additionalProperties: false }),
  Type.Object(
    {
      kind: Type.Literal("weekly"),
      ...calendarTime,
      weekdays: Type.Array(Type.Integer({ minimum: 1, maximum: 7 }), {
        minItems: 1,
        maxItems: 7,
        uniqueItems: true
      })
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      kind: Type.Literal("interval"),
      minutes: Type.Integer({ minimum: 5, maximum: 525_600 }),
      anchorAt: timestamp
    },
    { additionalProperties: false }
  )
]);

export const UserAutomationContextReferenceSchema = Type.Union([
  Type.Object(
    { kind: Type.Literal("document"), documentId: UuidSchema },
    { additionalProperties: false }
  ),
  Type.Object(
    { kind: Type.Literal("knowledge_base"), knowledgeBaseId: UuidSchema },
    { additionalProperties: false }
  )
]);
export const UserAutomationExecutionEnvironmentSchema = Type.Object(
  { deviceId: UuidSchema },
  { additionalProperties: false }
);

const configuration = {
  name,
  prompt,
  schedule: UserAutomationScheduleSchema,
  timezone,
  contextReferences: Type.Array(UserAutomationContextReferenceSchema, {
    maxItems: 32,
    uniqueItems: true
  }),
  executionEnvironment: Type.Union([UserAutomationExecutionEnvironmentSchema, Type.Null()]),
  enabled: Type.Boolean()
};

export const UserAutomationScopeSchema = Type.Object(
  {
    ownerPrincipalId: UuidSchema,
    agentId: UuidSchema,
    roomId: MatrixRoomIdSchema
  },
  { additionalProperties: false }
);

export const UserAutomationCreateSchema = Type.Object(
  {
    automationId: UuidSchema,
    ...configuration
  },
  { additionalProperties: false }
);

export const UserAutomationUpdateSchema = Type.Object(
  {
    revision,
    name: Type.Optional(name),
    prompt: Type.Optional(prompt),
    schedule: Type.Optional(UserAutomationScheduleSchema),
    timezone: Type.Optional(timezone),
    contextReferences: Type.Optional(configuration.contextReferences),
    executionEnvironment: Type.Optional(configuration.executionEnvironment),
    enabled: Type.Optional(Type.Boolean())
  },
  { additionalProperties: false, minProperties: 2 }
);

export const UserAutomationSchema = Type.Object(
  {
    ...UserAutomationScopeSchema.properties,
    ...UserAutomationCreateSchema.properties,
    revision,
    nextTriggerAt: Type.Union([timestamp, Type.Null()]),
    createdAt: timestamp,
    updatedAt: timestamp
  },
  { additionalProperties: false }
);

// This marker deliberately has no sourceEventId: Matrix assigns the root ID after sending.
export const UserAutomationRootSchema = Type.Object(
  {
    version: Type.Literal("1.0"),
    agentKey: AgentKeySchema,
    automationId: UuidSchema,
    revision,
    scheduledAt: timestamp,
    timezone,
    name,
    prompt
  },
  { additionalProperties: false }
);

export const UserAutomationDispatchSchema = Type.Object(
  {
    automationId: UuidSchema,
    revision,
    scheduledAt: timestamp,
    discoveredAt: timestamp,
    expiresAt: timestamp
  },
  { additionalProperties: false }
);

export const UserAutomationSkipReasonSchema = Type.Union([
  Type.Literal("capacity"),
  Type.Literal("overlap"),
  Type.Literal("expired")
]);

export const UserAutomationResultSchema = Type.Object(
  {
    automationId: UuidSchema,
    revision,
    scheduledAt: timestamp,
    fact: Type.Union([
      Type.Object({ kind: Type.Literal("timed_out") }, { additionalProperties: false }),
      Type.Object(
        {
          kind: Type.Literal("skipped"),
          reason: UserAutomationSkipReasonSchema,
          discoveredAt: timestamp
        },
        { additionalProperties: false }
      ),
      Type.Object(
        {
          kind: Type.Literal("local_operation"),
          outcome: Type.Union([
            Type.Literal("not_executed"),
            Type.Literal("failed"),
            Type.Literal("unknown")
          ]),
          reason: Type.Union([
            Type.Literal("environment_missing"),
            Type.Literal("unresponsive"),
            Type.Literal("busy"),
            Type.Literal("connection_lost"),
            Type.Literal("tool_missing"),
            Type.Literal("human_required"),
            Type.Literal("input_missing"),
            Type.Literal("tool_changed"),
            Type.Literal("execution_failed"),
            Type.Literal("result_untrusted")
          ]),
          effect: Type.Union([
            Type.Literal("none"),
            Type.Literal("possible"),
            Type.Literal("confirmed")
          ])
        },
        { additionalProperties: false }
      )
    ])
  },
  { additionalProperties: false }
);
export type UserAutomationResult = Static<typeof UserAutomationResultSchema>;

export const UserAutomationListSchema = Type.Object(
  {
    afterId: Type.Optional(UuidSchema),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 }))
  },
  { additionalProperties: false }
);

export const UserAutomationListResultSchema = Type.Object(
  {
    items: Type.Array(UserAutomationSchema, { maxItems: 100 }),
    nextCursor: Type.Union([UuidSchema, Type.Null()])
  },
  { additionalProperties: false }
);

export const UserAutomationCancelSchema = Type.Object(
  { revision },
  { additionalProperties: false }
);

export type UserAutomationList = Static<typeof UserAutomationListSchema>;
export type UserAutomationListResult = Static<typeof UserAutomationListResultSchema>;

export const UserAutomationAdmissionSchema = Type.Object(
  {
    automation: UserAutomationSchema,
    ownerMxid: Type.String({ pattern: "^@[^\\s]+:[^\\s]+$", maxLength: 512 })
  },
  { additionalProperties: false }
);
export type UserAutomationAdmission = Static<typeof UserAutomationAdmissionSchema>;

export type UserAutomationSchedule = Static<typeof UserAutomationScheduleSchema>;
export type UserAutomationScope = Static<typeof UserAutomationScopeSchema>;
export type UserAutomationCreate = Static<typeof UserAutomationCreateSchema>;
export type UserAutomationUpdate = Static<typeof UserAutomationUpdateSchema>;
export type UserAutomation = Static<typeof UserAutomationSchema>;
export type UserAutomationRoot = Static<typeof UserAutomationRootSchema>;
export type UserAutomationDispatch = Static<typeof UserAutomationDispatchSchema>;
